import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { normalizeSeed, randomWordGridSeed } from '~/boggle/engine/core'
import type { BoggleGameModeId } from '~/boggle/gameModes'
import {
  clearBoggleActiveGameModeFromStorage,
  readBoggleActiveGameModeFromStorage,
  writeBoggleActiveGameModeToStorage,
} from '~/boggle/ui/storage/activeGameModeStorage'
import { captureBoggleModeChanged } from '~/boggle/telemetry/boggleTelemetry'

export type BoggleChallengePhase = 'practice' | 'reveal' | 'run'

type BoggleModeSessionContextValue = {
  /**
   * Non-null while the player is on `/games/$gameModeId` or during an active standard run
   * on `/` after `persistGameModeFromGamesRoute`. Catalog, hub landing, challenges, and
   * playground clear hosted mode to null when not in a standard run.
   */
  activeGameModeId: BoggleGameModeId | null
  /**
   * Last browsed mode from `/games/$gameModeId` or `/` during an active run (persisted).
   * Null on hub landing after `clearPreferredGameMode`.
   */
  preferredGameModeId: BoggleGameModeId | null
  isStandardRunActive: boolean
  standardSeed: number
  /** Mode for the in-progress standard run (set when starting from a game-mode route). */
  standardRunGameModeId: BoggleGameModeId | null
  challengeRollSeed: number
  challengePhase: BoggleChallengePhase
  isDevMode: boolean
  startStandardRun: (gameModeId: BoggleGameModeId, seed?: number | string) => void
  endStandardRun: () => void
  prepareChallengeRun: () => void
  newStandardGame: (seed?: number | string) => void
  newChallengeGame: () => void
  persistGameModeFromGamesRoute: (gameModeId: BoggleGameModeId) => void
  clearRouteHostedGameMode: () => void
  clearPreferredGameMode: () => void
  setChallengePhase: (phase: BoggleChallengePhase) => void
}

const ModeSessionContext = createContext<BoggleModeSessionContextValue | null>(null)

export function ModeSessionProvider({
  children,
  isDevMode,
}: {
  children: ReactNode
  isDevMode: boolean
}) {
  const [preferredGameModeId, setPreferredGameModeId] = useState<BoggleGameModeId | null>(
    () => readBoggleActiveGameModeFromStorage(),
  )
  const [activeGameModeId, setActiveGameModeId] = useState<BoggleGameModeId | null>(null)
  const [standardRunGameModeId, setStandardRunGameModeId] =
    useState<BoggleGameModeId | null>(null)
  const [isStandardRunActive, setIsStandardRunActive] = useState(false)
  const [standardSeed, setStandardSeed] = useState(() => randomWordGridSeed())
  const [challengeRollSeed, setChallengeRollSeed] = useState(() => randomWordGridSeed())
  const [challengePhase, setChallengePhase] = useState<BoggleChallengePhase>('reveal')

  const persistGameModeFromGamesRoute = useCallback(
    (gameModeId: BoggleGameModeId) => {
      const previous = preferredGameModeId
      const next = writeBoggleActiveGameModeToStorage(gameModeId)
      if (!isDevMode && next !== previous) {
        captureBoggleModeChanged({
          gameModeId: next,
          previousGameModeId: previous,
        })
      }
      setPreferredGameModeId(next)
      setActiveGameModeId(next)
    },
    [isDevMode, preferredGameModeId],
  )

  const clearRouteHostedGameMode = useCallback(() => {
    setActiveGameModeId(null)
  }, [])

  const clearPreferredGameMode = useCallback(() => {
    clearBoggleActiveGameModeFromStorage()
    setPreferredGameModeId(null)
  }, [])

  const startStandardRun = useCallback(
    (gameModeId: BoggleGameModeId, seed?: number | string) => {
      setStandardRunGameModeId(gameModeId)
      setStandardSeed(seed === undefined ? randomWordGridSeed() : normalizeSeed(seed))
      setIsStandardRunActive(true)
    },
    [],
  )

  const endStandardRun = useCallback(() => {
    setIsStandardRunActive(false)
    setStandardRunGameModeId(null)
  }, [])

  const prepareChallengeRun = useCallback(() => {
    setChallengeRollSeed(randomWordGridSeed())
    setChallengePhase('reveal')
  }, [])

  const newStandardGame = useCallback((seed?: number | string) => {
    setStandardSeed(seed === undefined ? randomWordGridSeed() : normalizeSeed(seed))
  }, [])

  const newChallengeGame = useCallback(() => {
    setChallengeRollSeed(randomWordGridSeed())
    setChallengePhase('reveal')
  }, [])

  const value = useMemo(
    () => ({
      activeGameModeId,
      preferredGameModeId,
      isStandardRunActive,
      standardSeed,
      standardRunGameModeId,
      challengeRollSeed,
      challengePhase,
      isDevMode,
      startStandardRun,
      endStandardRun,
      prepareChallengeRun,
      newStandardGame,
      newChallengeGame,
      persistGameModeFromGamesRoute,
      clearRouteHostedGameMode,
      clearPreferredGameMode,
      setChallengePhase,
    }),
    [
      activeGameModeId,
      challengePhase,
      challengeRollSeed,
      clearPreferredGameMode,
      clearRouteHostedGameMode,
      endStandardRun,
      isDevMode,
      isStandardRunActive,
      newChallengeGame,
      newStandardGame,
      persistGameModeFromGamesRoute,
      prepareChallengeRun,
      preferredGameModeId,
      standardRunGameModeId,
      startStandardRun,
      standardSeed,
    ],
  )

  return (
    <ModeSessionContext.Provider value={value}>{children}</ModeSessionContext.Provider>
  )
}

export function useBoggleModeSession() {
  const ctx = useContext(ModeSessionContext)
  if (!ctx) {
    throw new Error('useBoggleModeSession must be used under ModeSessionProvider')
  }
  return ctx
}
